import { downloadSecondaryAnalysisPolygonsForStudy } from 'api/customerResults';
import { DownloadFilesModal } from 'components/atoms/DownloadFilesModal';
import { Study } from 'interfaces/study';
import { isEmpty, omit, some } from 'lodash';
import React, { useMemo } from 'react';
import { useCasesParams } from 'utils/useCasesParams';
import { manualSelectionFilters, paginationAndSortingParams } from 'utils/useCasesParams/constants';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import StudyExportMenuItem from './StudyExportMenuItem';
import { StudyExportOperation } from 'services/export/useStudyExportController';

interface SecondaryAnalysisExportMenuItemsProps {
  study?: Study;
  immediateExportDisabled?: boolean;
  startImmediateExport?: (operation: StudyExportOperation) => Promise<boolean>;
}

const SecondaryAnalysisExportMenuItems: React.FunctionComponent<SecondaryAnalysisExportMenuItemsProps> = ({
  study,
  immediateExportDisabled = false,
  startImmediateExport = async ({ run }) => {
    await run(new AbortController().signal, () => {});
    return true;
  },
}) => {
  const activeStudyId = study?.id;

  const { areFiltersAppliedExceptStudy } = useEncodedFilters();

  const { casesParams } = useCasesParams();

  const isSlidesMode = casesParams?.slidesMode;

  const hasSpecificSelections = some(manualSelectionFilters, (filterName) => !isEmpty(casesParams?.[filterName]));

  const { labId } = useCurrentLabId();

  const [secondaryAnalysisBundleUrl, setSecondaryAnalysisBundleUrl] = React.useState<string | null>(null);

  const secondaryAnalysisBundleBlobName = `secondary_analysis_polygons_${activeStudyId}.zip`;

  const secondaryAnalysisFilesToDownload = useMemo(
    () =>
      secondaryAnalysisBundleUrl
        ? [
            {
              title: secondaryAnalysisBundleBlobName,
              url: secondaryAnalysisBundleUrl!,
              fileName: secondaryAnalysisBundleBlobName,
            },
          ]
        : undefined,
    [secondaryAnalysisBundleUrl, secondaryAnalysisBundleBlobName]
  );

  const casesParamsWithoutPaginationAndSorting = omit(casesParams, paginationAndSortingParams);
  const casesParamsWithoutSelections = omit(casesParamsWithoutPaginationAndSorting, manualSelectionFilters);

  const exportNonExcludedSecondaryAnalysisPolygonsParams = { labId, studyId: activeStudyId };

  const exportAllSecondaryAnalysisPolygonsParams = {
    labId,
    studyId: activeStudyId,
    queryObject: {
      studyId: activeStudyId,
      labId,
      filters: {
        studyId: activeStudyId,
      },
    },
  };

  const exportSecondaryAnalysisPolygonsFilteredWithoutSelectionsParams = {
    labId,
    studyId: activeStudyId,
    queryObject: casesParamsWithoutSelections,
  };

  const exportSecondaryAnalysisPolygonsFilteredWithSelectionsParams = {
    labId,
    studyId: activeStudyId,
    queryObject: casesParamsWithoutPaginationAndSorting,
  };

  const startPolygonExport = (params: Parameters<typeof downloadSecondaryAnalysisPolygonsForStudy>[0]) =>
    startImmediateExport({
      title: 'Secondary analysis polygons export',
      canCancel: true,
      run: async (signal) => {
        const blob = await downloadSecondaryAnalysisPolygonsForStudy({ ...params, signal });
        try {
          setSecondaryAnalysisBundleUrl(URL.createObjectURL(blob));
        } catch (error) {
          console.error('Error downloading secondary analysis polygons:', { error, blob });
          throw error;
        }
      },
    });

  return (
    study?.hasFeatures && (
      <>
        <StudyExportMenuItem
          disabled={!study?.allowExport || immediateExportDisabled}
          disabledMessage={'Exports are disabled for this study'}
          onClick={() => void startPolygonExport(exportNonExcludedSecondaryAnalysisPolygonsParams)}
          title="Secondary analysis polygons"
          secondaryText="Whole study without excluded cases"
          tooltip={'If available, export secondary analysis polygons for this study'}
        />
        <StudyExportMenuItem
          disabled={!study?.allowExport || immediateExportDisabled}
          disabledMessage={'Exports are disabled for this study'}
          onClick={() => void startPolygonExport(exportAllSecondaryAnalysisPolygonsParams)}
          title="Secondary analysis polygons"
          secondaryText="Whole study with excluded cases"
          tooltip={'If available, export secondary analysis polygons for this study'}
        />
        {areFiltersAppliedExceptStudy && (
          <StudyExportMenuItem
            disabled={!study?.allowExport || immediateExportDisabled || isEmpty(casesParamsWithoutSelections.filters)}
            disabledMessage={
              !study?.allowExport
                ? 'Exports are disabled for this study'
                : 'No filters applied (except study) to export secondary analysis polygons'
            }
            onClick={() => void startPolygonExport(exportSecondaryAnalysisPolygonsFilteredWithoutSelectionsParams)}
            title="Secondary analysis polygons"
            secondaryText={`${isSlidesMode ? 'Slides' : 'Cases'} matching current filters`}
            tooltip={
              !isEmpty(casesParamsWithoutSelections.filters)
                ? `If available, export secondary analysis polygons for ${
                    isSlidesMode ? 'slides' : 'cases'
                  } matching the following criteria`
                : ''
            }
          />
        )}
        {hasSpecificSelections && (
          <StudyExportMenuItem
            disabled={!study?.allowExport || immediateExportDisabled || !hasSpecificSelections}
            disabledMessage={
              !study?.allowExport
                ? 'Exports are disabled for this study'
                : `No specific ${isSlidesMode ? 'slides' : 'cases'} selected to export secondary analysis polygons`
            }
            onClick={() => void startPolygonExport(exportSecondaryAnalysisPolygonsFilteredWithSelectionsParams)}
            title="Secondary analysis polygons"
            secondaryText={`Only selected ${isSlidesMode ? 'slides' : 'cases'}`}
            tooltip={
              hasSpecificSelections
                ? `If available, export secondary analysis polygons for selected ${isSlidesMode ? 'slides' : 'cases'}`
                : ''
            }
          />
        )}
        {Boolean(secondaryAnalysisFilesToDownload) && (
          <DownloadFilesModal
            open={Boolean(secondaryAnalysisFilesToDownload)}
            onClose={() => setSecondaryAnalysisBundleUrl(null)}
            filesToDownload={secondaryAnalysisFilesToDownload}
            dialogTitle="Download requested secondary analysis polygons"
          />
        )}
      </>
    )
  );
};

export default SecondaryAnalysisExportMenuItems;
